<script setup>
import apiClient from '@/apiClient.js'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useBreadcrumbStore } from '@/stores/BreadcrumbStore'
import { useRoute, useRouter } from 'vue-router'
import InteractiveTable from '@/components/InteractiveTable'
import DateField from '@/components/DateField.vue'
import ReferenceComments from '@/components/ReferenceComments'
import ReferenceHistory from '@/components/ReferenceHistory'
import dayjs from 'dayjs'
import * as Icon from '@heroicons/vue/24/solid'
var advancedFormat = require('dayjs/plugin/advancedFormat')
dayjs.extend(advancedFormat)

import { v4 as uuidv4 } from 'uuid'
import SearchCustomer from '@/components/SearchCustomer.vue'

const apiUrl = process.env.VUE_APP_API_URL
const route = useRoute()
const router = useRouter()
const breadcrumbStore = useBreadcrumbStore()
const invoice = ref({})
const invoiceSent = ref(false)
const documentFrame = ref(null)
const editable = ref(false)
const customerChanged = ref(false)
const customerPurchaseOrders = ref([])
const newPurchaseOrderNumber = ref('')
const purchaseOrderSaving = ref(false)
const purchaseOrderError = ref('')
const history = ref(null)
const stripeInvoiceLoading = ref(false)
const stripeRefundLoading = ref(false)
const stripeRecipientId = ref(null)
const stripeInvoiceModalOpen = ref(false)
const stripeInvoiceError = ref('')
const paidModalOpen = ref(false)
const paidReceiptRequested = ref(false)
const paidReceiptRecipientIds = ref([])
const paidModalSaving = ref(false)
const paidModalError = ref('')
const receiptModalOpen = ref(false)
const receiptSending = ref(false)
const receiptModalError = ref('')
const receiptRecipientIds = ref([])
const actionMenuOpen = ref(false)
const actionMenuRef = ref(null)
const approvalLoading = ref(false)
const approvalError = ref('')
const autoSendRecipientIds = ref([])
const emailCommentOpen = ref(false)
const emailContent = ref({
  subject: '',
  body: '',
  recipients: new Set(),
})
const pdfTimestamp = ref(Date.now())
const stripeRecipients = computed(
  () =>
    invoice.value?.reference?.customer?.contacts?.filter(contact =>
      Boolean(contact?.email?.trim()),
    ) ?? [],
)
const canRefundStripePayment = computed(
  () =>
    Boolean(invoice.value?.paid_at) &&
    Boolean(invoice.value?.stripe_payment_intent_id),
)
const effectivePurchaseOrderNumber = computed(() =>
  invoice.value?.customer_purchase_order?.po_number
  || invoice.value?.effective_purchase_order
  || invoice.value?.purchase_order
  || '',
)
const invoiceContacts = computed(() => {
  const contacts = invoice.value?.reference?.customer?.contacts ?? []
  const primaryContact = invoice.value?.reference?.customer?.contact
  const byId = new Map()

  if (primaryContact?.id) {
    byId.set(Number(primaryContact.id), primaryContact)
  }

  contacts.forEach(contact => {
    if (contact?.id) {
      byId.set(Number(contact.id), contact)
    }
  })

  return Array.from(byId.values())
})
const receiptRecipientContacts = computed(() => {
  const ids = Array.isArray(invoice.value?.sent_recipient_contact_ids)
    ? invoice.value.sent_recipient_contact_ids.map(id => Number(id))
    : []
  const contactsById = new Map(invoiceContacts.value.map(contact => [Number(contact.id), contact]))

  return ids
    .map(id => contactsById.get(id))
    .filter(contact => contact?.email?.trim())
})
const hasReceiptRecipients = computed(() => receiptRecipientContacts.value.length > 0)
const selectableReceiptContacts = computed(() =>
  invoiceContacts.value.filter(contact => contact?.email?.trim()),
)
const selectedPaidReceiptContacts = computed(() => {
  const selectedIds = paidReceiptRecipientIds.value.map(id => Number(id))
  const contactsById = new Map(selectableReceiptContacts.value.map(contact => [Number(contact.id), contact]))

  return selectedIds
    .map(id => contactsById.get(id))
    .filter(Boolean)
})
const paidReceiptCanSend = computed(() =>
  !paidReceiptRequested.value ||
  hasReceiptRecipients.value ||
  selectedPaidReceiptContacts.value.length > 0,
)
const approvedRecipientContacts = computed(() => {
  const ids = Array.isArray(invoice.value?.approved_recipient_contact_ids)
    ? invoice.value.approved_recipient_contact_ids.map(id => Number(id))
    : []
  const contactsById = new Map(invoiceContacts.value.map(contact => [Number(contact.id), contact]))

  return ids
    .map(id => contactsById.get(id))
    .filter(contact => contact?.email?.trim())
})
const selectableAutoSendContacts = computed(() =>
  invoiceContacts.value.filter(contact => contact?.email?.trim()),
)
const selectedAutoSendContacts = computed(() => {
  const ids = autoSendRecipientIds.value.map(id => Number(id))
  const contactsById = new Map(selectableAutoSendContacts.value.map(contact => [Number(contact.id), contact]))

  return ids
    .map(id => contactsById.get(id))
    .filter(Boolean)
})
const autoSendDisabledReason = computed(() => {
  if (invoice.value?.sent_at) {
    return 'This invoice has already been sent.'
  }
  if (invoice.value?.paid_at) {
    return 'Paid invoices do not need automatic sending.'
  }
  if (selectableAutoSendContacts.value.length < 1) {
    return 'Add a customer contact with an email address before approving automatic sending.'
  }
  if (autoSendRecipientIds.value.length < 1) {
    return 'Select at least one automatic sending recipient.'
  }
  return ''
})
const canApproveAutoSend = computed(() =>
  !approvalLoading.value && !autoSendDisabledReason.value,
)

onMounted(() => {
  getInvoice()
  document.addEventListener('click', handleDocumentClick)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleDocumentClick)
})

const initializeStripeRecipient = () => {
  const recipients = stripeRecipients.value
  if (recipients.length === 0) {
    stripeRecipientId.value = null
    return
  }

  if (
    stripeRecipientId.value &&
    recipients.some(contact => contact.id === stripeRecipientId.value)
  ) {
    return
  }

  const primaryContactId = invoice.value?.reference?.customer?.contact?.id
  const primaryContact = recipients.find(contact => contact.id === primaryContactId)
  stripeRecipientId.value = primaryContact?.id ?? recipients[0]?.id ?? null
}

const initializeAutoSendRecipients = () => {
  const recipients = selectableAutoSendContacts.value
  if (recipients.length === 0) {
    autoSendRecipientIds.value = []
    return
  }

  const approvedIds = Array.isArray(invoice.value?.approved_recipient_contact_ids)
    ? invoice.value.approved_recipient_contact_ids.map(id => Number(id))
    : []
  const validApprovedIds = approvedIds.filter(id =>
    recipients.some(contact => Number(contact.id) === id),
  )

  if (validApprovedIds.length > 0) {
    autoSendRecipientIds.value = validApprovedIds
    return
  }

  const primaryContactId = Number(invoice.value?.reference?.customer?.contact?.id ?? 0)
  const primaryContact = recipients.find(contact => Number(contact.id) === primaryContactId)
  autoSendRecipientIds.value = [primaryContact?.id ?? recipients[0]?.id]
}

const getInvoice = () => {
  apiClient
    .get('/api/invoice/' + route.params.id)
    .then(r => {
      invoice.value = r.data
      emailContent.value = {
        ...emailContent.value,
        subject: r.data.email_template?.subject ?? '',
        body: emailCommentOpen.value
          ? emailContent.value.body
          : (r.data.email_template?.body ?? ''),
      }
      breadcrumbStore.setValue(invoice.value?.reference?.name ?? '')
      initializeStripeRecipient()
      initializeAutoSendRecipients()
      loadCustomerPurchaseOrders()
    })
    .catch(e => { })
}

const loadCustomerPurchaseOrders = () => {
  const customerId = invoice.value?.reference?.customer?.id
  if (!customerId) {
    customerPurchaseOrders.value = []
    return
  }

  apiClient
    .get('/api/customer/' + customerId + '/purchase-orders')
    .then(r => {
      customerPurchaseOrders.value = r.data ?? []
    })
    .catch(e => {
      customerPurchaseOrders.value = []
    })
}

const createPurchaseOrder = async () => {
  const customerId = invoice.value?.reference?.customer?.id
  const poNumber = newPurchaseOrderNumber.value.trim()

  if (!customerId || !poNumber || purchaseOrderSaving.value) {
    return
  }

  purchaseOrderSaving.value = true
  purchaseOrderError.value = ''

  try {
    const { data } = await apiClient.post('/api/customer/' + customerId + '/purchase-orders', {
      po_number: poNumber,
    })
    customerPurchaseOrders.value = [data, ...customerPurchaseOrders.value]
    invoice.value.customer_purchase_order_id = data.id
    invoice.value.customer_purchase_order = data
    invoice.value.purchase_order = data.po_number
    newPurchaseOrderNumber.value = ''
  } catch (error) {
    purchaseOrderError.value = error?.response?.data?.message ?? 'Unable to create purchase order.'
  } finally {
    purchaseOrderSaving.value = false
  }
}

const edit = () => (editable.value = true)

const save = () => {
  editable.value = false
  apiClient
    .post('/api/invoice/' + invoice.value.id, {
      _method: 'PUT',
      ...invoice.value,
    })
    .then(r => {
      getInvoice()
      refreshIframe()
    })
    .catch(e => { })

  if (customerChanged.value === true) {
    apiClient
      .post('/api/reference/' + invoice.value.reference.id, {
        _method: 'PUT',
        customer_id: invoice.value.reference.customer.id,
      })
      .catch(e => { })
  }

  setTimeout(() => {
    history.value?.refresh()
    refreshIframe()
  }, 1000)
}

const closeActionMenu = () => {
  actionMenuOpen.value = false
}

const toggleActionMenu = () => {
  actionMenuOpen.value = !actionMenuOpen.value
}

const handleDocumentClick = event => {
  if (actionMenuRef.value?.contains(event.target)) {
    return
  }

  closeActionMenu()
}

const runInvoiceActionItem = async item => {
  if (item.disabled) {
    return
  }

  closeActionMenu()
  await item.action()
}

const contactLabel = contact => {
  const name = contact?.name?.trim()
  const email = contact?.email?.trim()

  if (name && email) {
    return `${name} <${email}>`
  }

  return name || email || 'Unknown contact'
}

const openPaidModal = () => {
  paidReceiptRequested.value = false
  paidReceiptRecipientIds.value = []
  paidModalError.value = ''
  paidModalOpen.value = true
}

const closePaidModal = () => {
  if (paidModalSaving.value) {
    return
  }
  paidModalOpen.value = false
}

const setPaid = () => {
  if (paidModalSaving.value) {
    return
  }

  paidModalSaving.value = true
  paidModalError.value = ''

  apiClient
    .post('/api/invoice/' + invoice.value.id + '/paid', {
      _method: 'PUT',
      send_receipt: paidReceiptRequested.value,
      receipt_recipients: paidReceiptRequested.value && !hasReceiptRecipients.value
        ? paidReceiptRecipientIds.value
        : undefined,
    })
    .then(() => {
      window.triggerConfetti?.()
      paidModalOpen.value = false
      getInvoice()
      history.value?.refresh()
    })
    .catch(e => {
      paidModalError.value = e?.response?.data?.message ?? 'Unable to mark invoice as paid.'
    })
    .finally(() => {
      paidModalSaving.value = false
    })
}

const openReceiptModal = () => {
  receiptModalError.value = ''
  receiptRecipientIds.value = []
  receiptModalOpen.value = true
}

const closeReceiptModal = () => {
  if (receiptSending.value) {
    return
  }
  receiptModalOpen.value = false
}

const sendPaymentReceipt = () => {
  if (receiptSending.value) {
    return
  }

  receiptSending.value = true
  receiptModalError.value = ''

  apiClient
    .post('/api/invoice/' + invoice.value.id + '/receipt', {
      recipients: !hasReceiptRecipients.value ? receiptRecipientIds.value : undefined,
    })
    .then(() => {
      receiptModalOpen.value = false
      getInvoice()
      history.value?.refresh()
    })
    .catch(e => {
      receiptModalError.value = e?.response?.data?.message ?? 'Unable to send payment receipt.'
    })
    .finally(() => {
      receiptSending.value = false
    })
}

const sendStripeInvoice = () => {
  if (stripeInvoiceLoading.value || !invoice.value?.id) {
    return
  }

  if (!stripeRecipientId.value) {
    stripeInvoiceError.value = 'Select a Stripe recipient with a valid email first.'
    return
  }

  stripeInvoiceLoading.value = true
  stripeInvoiceError.value = ''
  apiClient
    .post('/api/invoice/' + invoice.value.id + '/stripe/invoice', {
      recipient_contact_id: stripeRecipientId.value,
    })
    .then(r => {
      alert('Stripe invoice emailed to customer.')
      stripeInvoiceModalOpen.value = false
      getInvoice()
    })
    .catch(e => {
      stripeInvoiceError.value = e?.response?.data?.message ?? 'Unable to send Stripe invoice.'
    })
    .finally(() => {
      stripeInvoiceLoading.value = false
    })
}

const openStripeInvoiceModal = () => {
  stripeInvoiceError.value = ''
  initializeStripeRecipient()
  stripeInvoiceModalOpen.value = true
}

const closeStripeInvoiceModal = () => {
  if (stripeInvoiceLoading.value) {
    return
  }
  stripeInvoiceModalOpen.value = false
}

const refundStripePayment = () => {
  if (stripeRefundLoading.value || !invoice.value?.id) {
    return
  }

  if (!confirm('Refund this Stripe payment in full and mark the invoice unpaid?')) {
    return
  }

  stripeRefundLoading.value = true
  apiClient
    .post('/api/invoice/' + invoice.value.id + '/stripe/refund')
    .then(() => {
      alert('Stripe payment refunded.')
      getInvoice()
      history.value?.refresh()
    })
    .catch(e => {
      alert(e?.response?.data?.message ?? 'Unable to refund Stripe payment.')
    })
    .finally(() => {
      stripeRefundLoading.value = false
    })
}

const setUnpaid = () => {
  if (!confirm('Are you sure you want to set this invoice as unpaid?')) {
    return
  }
  apiClient
    .post('/api/invoice/' + invoice.value.id + '/unpaid', {
      _method: 'PUT',
    })
    .then(() => {
      getInvoice()
    })
    .catch(e => { })
}

const deleteInvoice = async () => {
  if (!confirm('Are you sure you want to delete?')) {
    return
  }

  await apiClient.delete('/api/invoice/' + invoice.value.id)
  router.push('/invoice')
}

const toggleInvoiceFlag = () => {
  const previousStatus = invoice.value.flagged_at
  updateFlagged(!previousStatus)

  apiClient
    .put('/api/invoice/' + invoice.value.id + '/flag')
    .then(response => {
      updateFlagged(response.data.flagged_at)
    })
    .catch(() => {
      updateFlagged(previousStatus)
    })
}

const downloadInvoiceDocument = () => {
  apiClient
    .get('/api/invoice/' + invoice.value.id + '/document', {
      responseType: 'blob',
    })
    .then(response => {
      const blob = new Blob([response.data], { type: response.data.type })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${invoice.value.reference?.txt ?? 'invoice'}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    })
}

const invoiceActionItems = computed(() => {
  const items = [
    {
      key: 'download',
      label: 'Download PDF',
      icon: Icon.ArrowDownCircleIcon,
      classes: 'text-cyan-700 dark:text-cyan-300',
      disabled: !invoice.value?.id,
      action: downloadInvoiceDocument,
    },
  ]

  if (invoice.value?.paid_at) {
    items.push({
      key: 'unpaid',
      label: 'Mark Unpaid',
      icon: Icon.SparklesIcon,
      classes: 'text-rose-700 dark:text-rose-300',
      disabled: false,
      action: setUnpaid,
    })
    items.push({
      key: 'receipt',
      label: 'Send Receipt',
      icon: Icon.EnvelopeIcon,
      classes: 'text-cyan-700 dark:text-cyan-300',
      disabled: receiptSending.value,
      action: openReceiptModal,
    })
  } else {
    items.push({
      key: 'paid',
      label: 'Mark Paid',
      icon: Icon.SparklesIcon,
      classes: 'text-emerald-700 dark:text-emerald-300',
      disabled: paidModalSaving.value,
      action: openPaidModal,
    })
    items.push({
      key: 'stripe-invoice',
      label: stripeInvoiceLoading.value ? 'Sending Stripe Invoice...' : 'Send Stripe Invoice',
      icon: Icon.CreditCardIcon,
      classes: 'text-indigo-700 dark:text-indigo-300',
      disabled: stripeInvoiceLoading.value || stripeRecipients.value.length < 1,
      action: openStripeInvoiceModal,
    })
  }

  if (canRefundStripePayment.value) {
    items.push({
      key: 'refund-stripe',
      label: stripeRefundLoading.value ? 'Refunding Stripe...' : 'Refund Stripe',
      icon: Icon.ReceiptRefundIcon,
      classes: 'text-orange-700 dark:text-orange-300',
      disabled: stripeRefundLoading.value,
      action: refundStripePayment,
    })
  }

  items.push({
    key: 'flag',
    label: invoice.value?.flagged_at ? 'Unflag Invoice' : 'Flag Invoice',
    icon: Icon.FlagIcon,
    classes: 'text-slate-700 dark:text-slate-200',
    disabled: false,
    action: toggleInvoiceFlag,
  })

  items.push({
    key: 'delete',
    label: 'Delete Invoice',
    icon: Icon.TrashIcon,
    classes: 'text-rose-700 dark:text-rose-300',
    disabled: false,
    action: deleteInvoice,
  })

  return items
})

/*
    Add a blank entry form but do not save
*/
const addItem = () => {
  const today = new Date()
  invoice.value.items.push({
    date: today.toISOString().split('T')[0],
    description: '',
    subtotal: 0.0,
    vatable: 1,
    uuid: uuidv4(),
  })
}

const changeCustomer = customer => {
  invoice.value.reference.customer = customer
  customerChanged.value = true
}

/**
    Delete an invoice item
*/
const deleteItem = inItem => {
  if (inItem.id) {
    apiClient
      .delete('/api/invoice/' + invoice.value.id + '/item/' + inItem.id, {
        _method: 'DELETE',
      })
      .then(() => {
        getInvoice()
        refreshIframe()
        history.value?.refresh()
      })
      .catch(e => { })
  }
}

/**
    Save changes to an invoice item
 */
const saveItem = inItem => {
  if (inItem.id) {
    apiClient
      .post('/api/invoice/' + invoice.value.id + '/item/' + inItem.id, {
        _method: 'PUT',
        ...inItem,
      })
      .then(() => {
        getInvoice()
        refreshIframe()
      })
      .catch(e => { })
  }

  // Create new item
  else {
    inItem.invoice_id = invoice.value.id
    apiClient
      .post('/api/invoice/' + invoice.value.id + '/item', {
        ...inItem,
      })
      .then(() => {
        getInvoice()
        refreshIframe()
        history.value?.refresh()
      })
      .catch(e => { })
  }
}

/*
    Update invoice items which have been editted by TableList
*/
const updateItems = data => {
  invoice.value.items.push(data)
}

const invoicePaymentStatusLabel = invoice => {
  if (invoice?.payment_status_label) {
    return invoice.payment_status_label
  }

  if (!invoice?.sent_at && !invoice?.paid_at) {
    return 'Unsent'
  }

  return invoice?.paid_at ? 'Paid' : 'Unpaid'
}

const invoicePaymentStatusClass = invoice => {
  return invoicePaymentStatusLabel(invoice) === 'Paid'
    ? 'bg-pink-500'
    : 'bg-rose-500'
}

const collectionStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'promised', label: 'Promised' },
  { value: 'payment_plan', label: 'Payment plan' },
  { value: 'disputed', label: 'Disputed' },
  { value: 'on_hold', label: 'On hold' },
  { value: 'debt_recovery', label: 'Debt recovery' },
  { value: 'legal', label: 'Legal' },
  { value: 'doubtful', label: 'Doubtful' },
  { value: 'unrecoverable', label: 'Unrecoverable' },
  { value: 'written_off', label: 'Written off' },
]

const collectionStatusLabel = invoice => {
  if (invoice?.collection_status_label) {
    return invoice.collection_status_label
  }

  return collectionStatusOptions.find(option => option.value === invoice?.collection_status)?.label ?? 'Active'
}

const invoiceCollectionStatusClass = invoice => {
  const status = invoice?.collection_status ?? 'active'
  if (['doubtful', 'unrecoverable', 'written_off'].includes(status)) {
    return 'bg-zinc-600'
  }
  if (['debt_recovery', 'legal'].includes(status)) {
    return 'bg-amber-600'
  }
  if (['promised', 'payment_plan'].includes(status)) {
    return 'bg-emerald-600'
  }
  if (['disputed', 'on_hold'].includes(status)) {
    return 'bg-violet-600'
  }
  return 'bg-slate-600'
}

/**
  Update value of invoice flagged_at
  */
const updateFlagged = flagged_at => (invoice.value.flagged_at = flagged_at)

/**
 Toggle recipients for invoice email
 */
const toggleEmailRecipient = contact => {
  const id = contact.target.value
  const nextRecipients = new Set(emailContent.value.recipients)
  if (nextRecipients.has(id)) {
    nextRecipients.delete(id)
  } else {
    nextRecipients.add(id)
  }
  emailContent.value = {
    ...emailContent.value,
    recipients: nextRecipients,
  }
}

const openEmailComment = () => {
  emailCommentOpen.value = true
}

const removeEmailComment = () => {
  emailCommentOpen.value = false
  emailContent.value = {
    ...emailContent.value,
    body: '',
  }
}

/**
Request for invoice to be sent to given recipients, subject and body
*/
const sendInvoice = () => {
  const data = {
    recipients: Array.from(emailContent.value.recipients),
    subject: emailContent.value.subject,
    body: emailContent.value.body.trim(),
  }

  if (data.recipients.length < 1) {
    return alert('Oops.. you forgot to select who to send this to!')
  }

  apiClient
    .post('/api/invoice/' + invoice.value.id + '/email', data)
    .then(r => {
      invoiceSent.value = true
    })
    .catch(e => { })
}

const approveInvoiceForSending = () => {
  approvalError.value = ''

  if (autoSendDisabledReason.value) {
    approvalError.value = autoSendDisabledReason.value
    return
  }

  approvalLoading.value = true
  apiClient
    .put('/api/invoice/' + invoice.value.id + '/approve-sending', {
      recipients: autoSendRecipientIds.value,
    })
    .then(() => {
      getInvoice()
      history.value?.refresh()
    })
    .catch(e => {
      approvalError.value = e?.response?.data?.message ?? 'Unable to approve invoice for automatic sending.'
    })
    .finally(() => {
      approvalLoading.value = false
    })
}

const unapproveInvoiceForSending = () => {
  approvalError.value = ''
  approvalLoading.value = true
  apiClient
    .delete('/api/invoice/' + invoice.value.id + '/approve-sending')
    .then(() => {
      getInvoice()
      history.value?.refresh()
    })
    .catch(e => {
      approvalError.value = e?.response?.data?.message ?? 'Unable to clear automatic sending approval.'
    })
    .finally(() => {
      approvalLoading.value = false
    })
}

const iframeSrc = computed(
  () =>
    `${apiUrl}/api/invoice/${invoice.value.id}/document?${pdfTimestamp.value}`,
)

const refreshIframe = () => {
  pdfTimestamp.value = Date.now()
}
</script>
<template>
  <div>
    <form-box :header="invoice.reference?.name">
      <template v-slot:nav>
        <template v-if="invoice.id">
          <div class="relative" ref="actionMenuRef">
            <button
              type="button"
              class="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-700 shadow-sm hover:bg-cyan-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 dark:border-cyan-800 dark:bg-cyan-950/40 dark:text-cyan-200 dark:hover:bg-cyan-900/50"
              :aria-expanded="actionMenuOpen"
              aria-haspopup="menu"
              aria-label="Open invoice actions menu"
              @click.prevent.stop="toggleActionMenu"
            >
              <span>Actions</span>
              <Icon.ChevronDownIcon class="h-4 w-4" />
            </button>

            <div
              v-if="actionMenuOpen"
              class="absolute right-0 top-full z-40 mt-2 w-72 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800"
              role="menu"
            >
              <div class="px-4 py-3">
                <p class="text-sm font-semibold text-slate-900 dark:text-slate-100">Invoice Actions</p>
                <p class="text-xs text-slate-500 dark:text-slate-400">Quick actions for this invoice.</p>
              </div>
              <div class="h-px bg-slate-200 dark:bg-slate-700"></div>
              <div class="py-2">
                <button
                  v-for="item in invoiceActionItems"
                  :key="item.key"
                  type="button"
                  class="flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                  :class="item.classes"
                  :disabled="item.disabled"
                  role="menuitem"
                  @click.prevent="runInvoiceActionItem(item)"
                >
                  <component
                    v-if="item.icon"
                    :is="item.icon"
                    class="h-4 w-4 shrink-0"
                  />
                  <span>{{ item.label }}</span>
                </button>
              </div>
            </div>
          </div>
        </template>
      </template>
    </form-box>
    <form-box header="Details">
      <template v-slot:nav>
        <edit-button @click.prevent="edit" v-if="!editable"></edit-button>
        <save-button @save="save" @cancel="editable = false" title="Save" :hideCancel="false"
          v-if="editable"></save-button>
      </template>
      <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-full" v-if="invoice.id">
        <div>
          <label>Customer</label>
          <span class="label-customer"><router-link :to="'/customer/' + invoice.reference?.customer?.id">{{
            invoice.reference?.customer?.company
              }}</router-link></span>
          <SearchCustomer v-if="editable" :customer="invoice.reference?.customer" :resetAfterSelection="false"
            @selected="changeCustomer" />
        </div>

        <div>
          <label>Reference</label>
          <ReferenceNumber :reference="invoice.reference" />
        </div>

        <div>
          <label>Payment</label>
          <span class="label-clickable inline-block" :class="invoicePaymentStatusClass(invoice)"
            :title="invoice.paid_at ? dayjs(invoice.paid_at).format('LL') : null">
            {{ invoicePaymentStatusLabel(invoice) }}
          </span>
        </div>

        <div>
          <label>Purchase Order (PO)</label>
          <template v-if="editable">
            <select v-model="invoice.customer_purchase_order_id" class="w-full">
              <option :value="null">No linked PO</option>
              <option v-for="purchaseOrder in customerPurchaseOrders" :key="purchaseOrder.id" :value="purchaseOrder.id">
                {{ purchaseOrder.po_number }} · {{ purchaseOrder.status }}
              </option>
            </select>
            <div class="mt-2 flex gap-2">
              <input
                v-model="newPurchaseOrderNumber"
                class="min-w-0 flex-1"
                type="text"
                placeholder="Create PO number"
                @keydown.enter.prevent="createPurchaseOrder"
              />
              <button
                class="btn"
                type="button"
                :disabled="purchaseOrderSaving || !newPurchaseOrderNumber.trim()"
                @click.prevent="createPurchaseOrder"
              >
                Add
              </button>
            </div>
            <p v-if="purchaseOrderError" class="mt-1 text-xs text-red-600">{{ purchaseOrderError }}</p>
            <div class="mt-2">
              <label class="text-xs">Legacy PO text</label>
              <TextField :data="invoice.purchase_order" :editable="editable"
                @update="val => (invoice.purchase_order = val)" />
            </div>
          </template>
          <span v-else>{{ effectivePurchaseOrderNumber || 'not set' }}</span>
        </div>
        <div>
          <label>Subtotal</label>
          <span>&pound;
            <CurrencyOutput :price="invoice?.subtotal ?? 0" />
          </span>
        </div>
        <div>
          <label>VAT</label>
          <span>&pound;
            <CurrencyOutput :price="invoice?.vat ?? 0" />
          </span>
        </div>
        <div>
          <label>Total</label>
          <span>&pound;
            <CurrencyOutput :price="invoice.subtotal + invoice.vat" />
          </span>
        </div>
        <div>
          <label>Address</label>
          <select v-model="invoice.address_id" v-if="editable">
            <option value="">Default Address</option>
            <option v-for="address in invoice.reference.customer?.address" :key="address" :value="address.id"
              :disabled="!editable">
              {{ address.data }}
            </option>
          </select>
          <span v-else>{{ invoice.address_id ?? 'Default Address' }}</span>
        </div>
      </div>
    </form-box>

    <form-box header="Dates">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 max-w-full" v-if="invoice.id">
        <div>
          <label>Created</label>
          <DateField :data="invoice.created_at" />
        </div>
        <div>
          <label>Issue Date</label>
          <DateField :data="invoice.issue_date" :editable="editable" @update="val => (invoice.issue_date = val)" />
        </div>
        <div>
          <label>Sent Date</label>
          <DateField :data="invoice.sent_at" :editable="editable" @update="val => (invoice.sent_at = val)" />
        </div>
        <div>
          <label>Due Date</label>
          <DateField :data="invoice.expected_at" :editable="editable" @update="val => (invoice.expected_at = val)" />
        </div>
        <div>
          <label>Auto Send Approval</label>
          <span v-if="invoice.approved_for_sending_at" class="label-clickable inline-block bg-emerald-600">
            Approved {{ dayjs(invoice.approved_for_sending_at).format('DD/MM/YYYY') }}
          </span>
          <span v-else class="label-clickable inline-block bg-slate-500">
            Not approved
          </span>
        </div>
      </div>
    </form-box>

    <form-box header="Collection">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-full" v-if="invoice.id">
        <div>
          <label>Status</label>
          <select v-model="invoice.collection_status" class="input w-full" v-if="editable">
            <option v-for="option in collectionStatusOptions" :key="option.value" :value="option.value">
              {{ option.label }}
            </option>
          </select>
          <span class="label-clickable inline-block" :class="invoiceCollectionStatusClass(invoice)" v-else>
            {{ collectionStatusLabel(invoice) }}
          </span>
        </div>
        <div>
          <label>Next Action</label>
          <input type="date" v-model="invoice.collection_next_action_at" class="input w-full" v-if="editable" />
          <span v-else>
            {{ invoice.collection_next_action_at ? dayjs(invoice.collection_next_action_at).format('LL') : 'No action set' }}
          </span>
        </div>
        <div class="md:col-span-2">
          <label>Recovery Notes</label>
          <textarea v-model="invoice.collection_notes" class="w-full min-h-[7rem] rounded-lg" v-if="editable"></textarea>
          <p class="whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300" v-else>
            {{ invoice.collection_notes || 'No recovery notes' }}
          </p>
        </div>
      </div>
    </form-box>

    <form-box header="Items" v-if="invoice.items">
      <InteractiveTable :data="invoice.items" :cols="[
        {
          name: 'Billing Date',
          row: 'date',
          editable: true,
          type: 'date',
        },
        {
          name: 'Description',
          row: 'description',
          editable: true,
          type: 'textarea'
        },
        {
          name: 'Subtotal',
          row: 'subtotal',
          editable: true,
          type: 'currency',
        },
        {
          name: 'VAT',
          row: 'vatable',
          editable: true,
          type: 'checkbox',
        },
      ]" :addable="true" :deletable="true" :editable="true" @delete="deleteItem" @save="saveItem" />
    </form-box>

    <form-box header="Additional Information">
      <textarea v-model="invoice.additionalInformation" class="w-full" :disabled="!editable"
        placeholder="Additional information can be added to the invoice by editing this."></textarea>
      <small>This information will appear on a supplimentary page of the
        invoice.</small>
    </form-box>
    <form-box header="Send via Email">
      <div class="mb-4">
        <label class="block mb-1">Stripe recipient</label>
        <select class="w-full block" v-model="stripeRecipientId">
          <option :value="null">Select who to send Stripe invoice to</option>
          <option v-for="contact in stripeRecipients" :key="'stripe-' + contact.id" :value="contact.id">
            {{ contact.name }} &lt;{{ contact.email }}&gt;
          </option>
        </select>
        <small v-if="stripeRecipients.length < 1" class="text-red-600">
          Add a customer contact with an email address before sending a Stripe invoice.
        </small>
      </div>
      <div v-if="invoiceSent">Invoice has been successfully sent.</div>
      <div v-else>
        <div class="grid grid-cols-auto md:grid-cols-2 gap-4 mb-4">
          <div v-for="contact in invoice.reference?.customer?.contacts" :key="contact.id">
            <label class="select-none">
              <input type="checkbox" :value="contact.id" @click="toggleEmailRecipient" />
              {{ contact.name }} &lt;{{ contact.email }}&gt;
            </label>
          </div>
        </div>
        <div class="mb-4">
          <label class="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
            Email subject
          </label>
          <input type="text" class="block w-full" placeholder="Invoice email subject…"
            v-model="emailContent.subject" />
        </div>
        <div class="mb-4 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/40 sm:p-4">
          <div v-if="!emailCommentOpen" class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div class="text-sm font-medium text-slate-800 dark:text-slate-100">Additional comment</div>
              <p class="mt-1 text-xs text-slate-500">
                The standard invoice email is ready to send without an additional comment.
              </p>
            </div>
            <button
              class="btn btn-sm flex w-full items-center justify-center gap-2 sm:w-auto"
              type="button"
              @click.prevent="openEmailComment"
            >
              <Icon.ChatBubbleLeftEllipsisIcon class="icon" />
              Add an optional comment
            </button>
          </div>
          <div v-else>
            <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <label class="text-sm font-medium text-slate-800 dark:text-slate-100">
                Additional comment (optional)
              </label>
              <button
                class="self-start text-xs text-slate-500 underline hover:text-slate-700 dark:hover:text-slate-300 sm:self-auto"
                type="button"
                @click.prevent="removeEmailComment"
              >
                Remove comment
              </button>
            </div>
            <textarea
              class="mt-3 block min-h-32 w-full"
              v-model="emailContent.body"
              placeholder="Add any extra information you want to include with the invoice."
            ></textarea>
            <p class="mt-2 text-xs text-slate-500">Leave blank to send the standard invoice email only.</p>
          </div>
        </div>
        <div class="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button class="btn w-full sm:w-auto" @click.prevent="sendInvoice">Send</button>
          <button class="btn-plain w-full bg-indigo-600 sm:w-auto" @click.prevent="sendStripeInvoice"
            :disabled="stripeInvoiceLoading || !stripeRecipientId"
            v-if="!invoice.paid_at">
            <Icon.CreditCardIcon class="icon" />
            {{ stripeInvoiceLoading ? 'Sending...' : 'Send Stripe Invoice' }}
          </button>
        </div>
        <div class="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm dark:border-slate-700 dark:bg-slate-900/40">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div class="font-semibold text-slate-900 dark:text-slate-100">
                Automatic sending
              </div>
              <p class="mt-1 text-slate-600 dark:text-slate-300">
                Approved invoices will be emailed automatically when due if the customer has auto invoicing enabled.
              </p>
            </div>
            <button
              v-if="!invoice.approved_for_sending_at"
              class="btn-plain bg-emerald-600"
              type="button"
              :disabled="!canApproveAutoSend"
              :title="autoSendDisabledReason || 'Approve automatic invoice sending'"
              @click.prevent="approveInvoiceForSending"
            >
              {{ approvalLoading ? 'Approving...' : 'Approve Auto Send' }}
            </button>
            <button
              v-else
              class="btn"
              type="button"
              :disabled="approvalLoading"
              @click.prevent="unapproveInvoiceForSending"
            >
              {{ approvalLoading ? 'Clearing...' : 'Clear Approval' }}
            </button>
          </div>
          <div v-if="!invoice.approved_for_sending_at" class="mt-3 rounded-lg border border-white bg-white/70 p-3 dark:border-slate-700 dark:bg-slate-800/70">
            <div class="font-medium text-slate-800 dark:text-slate-100">Automatic sending recipients</div>
            <div v-if="selectableAutoSendContacts.length" class="mt-2 grid gap-2 md:grid-cols-2">
              <label
                v-for="contact in selectableAutoSendContacts"
                :key="'auto-send-contact-' + contact.id"
                class="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                <input
                  v-model="autoSendRecipientIds"
                  type="checkbox"
                  :value="contact.id"
                  class="mt-1 h-4 w-4"
                />
                <span>{{ contactLabel(contact) }}</span>
              </label>
            </div>
            <p v-if="autoSendDisabledReason" class="mt-2 text-amber-700 dark:text-amber-300">
              {{ autoSendDisabledReason }}
            </p>
          </div>
          <div v-if="invoice.approved_for_sending_at" class="mt-3 text-slate-600 dark:text-slate-300">
            <div>Recipients</div>
            <ul class="mt-1 list-disc pl-5">
              <li v-for="contact in approvedRecipientContacts" :key="'approved-recipient-' + contact.id">
                {{ contactLabel(contact) }}
              </li>
            </ul>
          </div>
          <p v-if="approvalError" class="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
            {{ approvalError }}
          </p>
        </div>
      </div>
    </form-box>

    <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <ReferenceComments v-if="invoice.reference" :reference_id="invoice.reference.id" />
      <ReferenceHistory ref="history" v-if="invoice.reference" :reference_id="invoice.reference.id" />
    </div>
    <br>

    <form-box header="Document">
      <template v-slot:nav>
        <DownloadButton :url="'/api/invoice/' + invoice.id + '/document'" :filename="invoice.reference?.txt + '.pdf'"
          mime="application / pdf" />
      </template>
      <iframe reference="documentFrame" v-if="invoice.id" :src="iframeSrc" class="w-full" height="800" />
    </form-box>

    <div
      v-if="paidModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4"
      @click.self="closePaidModal"
    >
      <div class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900">
        <div class="space-y-3">
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-500">
            Mark Invoice Paid
          </div>
          <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Mark this invoice as paid?
          </h2>
          <p class="text-sm leading-6 text-slate-600 dark:text-slate-300">
            This records the invoice payment date and updates the linked job history.
          </p>

          <label class="flex items-start gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-200">
            <input
              v-model="paidReceiptRequested"
              type="checkbox"
              class="mt-1 h-4 w-4 rounded border-slate-300"
            />
            <span>Email a payment receipt to the recorded invoice recipient{{ receiptRecipientContacts.length === 1 ? '' : 's' }}.</span>
          </label>

          <div
            v-if="paidReceiptRequested && hasReceiptRecipients"
            class="rounded-xl border px-4 py-3 text-sm"
            :class="'border-cyan-200 bg-cyan-50 text-cyan-900 dark:border-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-100'"
          >
            <div class="font-semibold">Receipt recipients</div>
            <ul class="mt-2 space-y-1">
              <li v-for="contact in receiptRecipientContacts" :key="'paid-receipt-' + contact.id">
                {{ contactLabel(contact) }}
              </li>
            </ul>
          </div>

          <div
            v-if="paidReceiptRequested && !hasReceiptRecipients"
            class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
          >
            <label class="block font-semibold">
              Select receipt recipient
              <select
                v-model="paidReceiptRecipientIds"
                multiple
                class="mt-2 min-h-[7rem] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                :disabled="paidModalSaving"
              >
                <option v-for="contact in selectableReceiptContacts" :key="'paid-select-' + contact.id" :value="contact.id">
                  {{ contactLabel(contact) }}
                </option>
              </select>
            </label>
            <p v-if="selectableReceiptContacts.length < 1" class="mt-2">
              Add a customer contact with an email address before sending a receipt.
            </p>
          </div>

          <p v-if="paidModalError" class="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
            {{ paidModalError }}
          </p>
        </div>

        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button class="btn" :disabled="paidModalSaving" @click.prevent="closePaidModal">
            Keep Unpaid
          </button>
          <button
            class="btn-plain bg-green-600"
            :disabled="paidModalSaving || !paidReceiptCanSend"
            @click.prevent="setPaid"
          >
            {{ paidModalSaving ? 'Saving...' : 'Mark Paid' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="stripeInvoiceModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4"
      @click.self="closeStripeInvoiceModal"
    >
      <div class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900">
        <div class="space-y-3">
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-500">
            Stripe Invoice
          </div>
          <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Send a Stripe invoice?
          </h2>
          <p class="text-sm leading-6 text-slate-600 dark:text-slate-300">
            Select the customer contact who should receive the Stripe payment email.
          </p>

          <label class="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Recipient
            <select
              class="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              v-model="stripeRecipientId"
              :disabled="stripeInvoiceLoading"
            >
              <option :value="null">Select who to send Stripe invoice to</option>
              <option v-for="contact in stripeRecipients" :key="'stripe-modal-' + contact.id" :value="contact.id">
                {{ contactLabel(contact) }}
              </option>
            </select>
          </label>

          <p v-if="stripeInvoiceError" class="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
            {{ stripeInvoiceError }}
          </p>
        </div>

        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button class="btn" :disabled="stripeInvoiceLoading" @click.prevent="closeStripeInvoiceModal">
            Cancel
          </button>
          <button
            class="btn-plain bg-indigo-600"
            :disabled="stripeInvoiceLoading || !stripeRecipientId"
            @click.prevent="sendStripeInvoice"
          >
            {{ stripeInvoiceLoading ? 'Sending...' : 'Send Stripe Invoice' }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="receiptModalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4"
      @click.self="closeReceiptModal"
    >
      <div class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900">
        <div class="space-y-3">
          <div class="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-500">
            Payment Receipt
          </div>
          <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Send a payment receipt?
          </h2>
          <p class="text-sm leading-6 text-slate-600 dark:text-slate-300">
            The receipt will be emailed to the contacts recorded when this invoice was sent.
          </p>

          <div
            v-if="hasReceiptRecipients"
            class="rounded-xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-cyan-900 dark:border-cyan-900 dark:bg-cyan-950/30 dark:text-cyan-100"
          >
            <div class="font-semibold">Receipt recipients</div>
            <ul class="mt-2 space-y-1">
              <li v-for="contact in receiptRecipientContacts" :key="'manual-receipt-' + contact.id">
                {{ contactLabel(contact) }}
              </li>
            </ul>
          </div>

          <div
            v-else
            class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
          >
            <label class="block font-semibold">
              Select receipt recipient
              <select
                v-model="receiptRecipientIds"
                multiple
                class="mt-2 min-h-[7rem] w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                :disabled="receiptSending"
              >
                <option v-for="contact in selectableReceiptContacts" :key="'manual-select-' + contact.id" :value="contact.id">
                  {{ contactLabel(contact) }}
                </option>
              </select>
            </label>
            <p v-if="selectableReceiptContacts.length < 1" class="mt-2">
              Add a customer contact with an email address before sending a receipt.
            </p>
          </div>

          <p v-if="receiptModalError" class="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
            {{ receiptModalError }}
          </p>
        </div>

        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button class="btn" :disabled="receiptSending" @click.prevent="closeReceiptModal">
            Cancel
          </button>
          <button
            class="btn-plain bg-cyan-600"
            :disabled="receiptSending || (!hasReceiptRecipients && receiptRecipientIds.length < 1)"
            @click.prevent="sendPaymentReceipt"
          >
            {{ receiptSending ? 'Sending...' : 'Send Receipt' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
